/**
 * 🔐 REQUEST SECURITY UTILITY
 * Encrypts and signs bet requests to prevent tampering and direct API calls
 * 
 * Security Features:
 * 1. AES-256-CBC encryption for payload (CryptoJS default, compatible with backend)
 * 2. HMAC-SHA256 signature for request verification
 * 3. Nonce + timestamp for replay attack prevention
 * 4. Server-side validation of all parameters
 * 
 * CRITICAL: Encryption keys must match backend exactly:
 * - REACT_APP_ENCRYPTION_KEY must match backend ENCRYPTION_KEY
 * - REACT_APP_SIGNING_KEY must match backend SIGNING_KEY
 */

import CryptoJS from 'crypto-js';

// 🔑 SECRET KEYS - Store these securely (environment variables in production)
// CRITICAL: These must match the server-side keys EXACTLY
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'reddyanna-secret-encryption-key-2024-secure-betting-platform';
const SIGNING_KEY = process.env.REACT_APP_SIGNING_KEY || 'reddyanna-secret-signing-key-2024-hmac-verification';

// Generate a random nonce (number used once)
const generateNonce = (): string => {
  return CryptoJS.lib.WordArray.random(16).toString();
};

// Get current timestamp
const getTimestamp = (): number => {
  return Math.floor(Date.now() / 1000);
};

/**
 * 🔒 Encrypt bet payload with AES-256
 * @param payload - The bet data to encrypt
 * @param timestamp - Optional timestamp to use (if not provided, generates new one)
 * @returns Encrypted payload with metadata
 */
export const encryptBetPayload = (payload: any, timestamp?: number): string => {
  try {
    console.log('🔐 Encrypting bet payload...');
    
    // Use provided timestamp or generate new one (for backward compatibility)
    const payloadTimestamp = timestamp || getTimestamp();
    
    // Add security metadata
    const securePayload = {
      data: payload,
      nonce: generateNonce(),
      timestamp: payloadTimestamp, // Use the provided timestamp
      version: '1.0'
    };
    
    // Convert to JSON string
    const jsonString = JSON.stringify(securePayload);
    
    // Encrypt with AES
    const encrypted = CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
    
    console.log('✅ Payload encrypted successfully');
    return encrypted;
  } catch (error) {
    console.error('❌ Encryption failed:', error);
    throw new Error('Failed to encrypt bet payload');
  }
};

/**
 * 🔏 Sign the encrypted payload with HMAC-SHA256
 * @param encryptedPayload - The encrypted data
 * @param userId - User ID for additional verification
 * @param timestamp - The timestamp to use for signing (must match request timestamp)
 * @returns HMAC signature
 */
export const signRequest = (encryptedPayload: string, userId: string, timestamp: number): string => {
  try {
    console.log('✍️ Signing request...');
    
    // Create signature data using the provided timestamp (must match request timestamp)
    const signatureData = `${encryptedPayload}|${userId}|${timestamp}`;
    
    // Generate HMAC signature
    const signature = CryptoJS.HmacSHA256(signatureData, SIGNING_KEY).toString();
    
    console.log('✅ Request signed successfully');
    return signature;
  } catch (error) {
    console.error('❌ Signing failed:', error);
    throw new Error('Failed to sign request');
  }
};

/**
 * 🛡️ Create secure bet request
 * Encrypts payload and adds signature header
 * @param betPayload - Original bet data
 * @param userId - User ID
 * @returns Secure request object
 */
export const createSecureBetRequest = (betPayload: any, userId: string) => {
  try {
    console.log('🛡️ Creating secure bet request...');
    
    // Step 1: Generate timestamp ONCE (must be same for encryption, signature, and request)
    const timestamp = getTimestamp();
    console.log('⏰ Generated timestamp:', timestamp);
    
    // Step 2: Encrypt the payload using the SAME timestamp
    const encryptedPayload = encryptBetPayload(betPayload, timestamp);
    
    // Step 3: Sign the encrypted payload using the SAME timestamp
    const signature = signRequest(encryptedPayload, userId, timestamp);
    
    // Step 4: Create secure request object with the SAME timestamp
    const secureRequest = {
      payload: encryptedPayload,
      signature: signature,
      userId: userId,
      timestamp: timestamp
    };
    
    console.log('✅ Secure bet request created');
    console.log('📦 Encrypted payload length:', encryptedPayload.length);
    console.log('✍️ Signature:', signature.substring(0, 20) + '...');
    console.log('⏰ Timestamp (used consistently):', timestamp);
    console.log('👤 User ID:', userId);
    
    return secureRequest;
  } catch (error) {
    console.error('❌ Failed to create secure request:', error);
    throw error;
  }
};

/**
 * 🔓 Decrypt response from server (for testing)
 * @param encryptedResponse - Encrypted response from server
 * @returns Decrypted data
 */
export const decryptResponse = (encryptedResponse: string): any => {
  try {
    console.log('🔓 Decrypting server response...');
    
    // Decrypt with AES
    const decrypted = CryptoJS.AES.decrypt(encryptedResponse, ENCRYPTION_KEY);
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    // Parse JSON
    const data = JSON.parse(decryptedString);
    
    console.log('✅ Response decrypted successfully');
    return data;
  } catch (error) {
    console.error('❌ Decryption failed:', error);
    throw new Error('Failed to decrypt server response');
  }
};

/**
 * 🚫 Validate request parameters before sending
 * Additional client-side validation to prevent obvious tampering
 * @param betPayload - Bet payload to validate
 * @returns Validation result
 */
export const validateBetPayload = (betPayload: any): { valid: boolean; error?: string } => {
  try {
    // Required fields
    const requiredFields = ['stack', 'odds', 'marketId', 'matchId', 'selectionId', 'isBack'];
    for (const field of requiredFields) {
      if (!(field in betPayload) || betPayload[field] === null || betPayload[field] === undefined) {
        return { valid: false, error: `Missing required field: ${field}` };
      }
    }
    
    // Validate numeric fields
    if (typeof betPayload.stack !== 'number' || betPayload.stack <= 0) {
      return { valid: false, error: 'Invalid stake amount' };
    }
    
    if (typeof betPayload.odds !== 'number' || betPayload.odds <= 1) {
      return { valid: false, error: 'Invalid odds value' };
    }
    
    // Validate IDs
    if (!betPayload.marketId || !betPayload.matchId || !betPayload.selectionId) {
      return { valid: false, error: 'Invalid market/match/selection IDs' };
    }
    
    // Validate bet type
    if (typeof betPayload.isBack !== 'boolean') {
      return { valid: false, error: 'Invalid bet type' };
    }
    
    console.log('✅ Client-side payload validation passed');
    return { valid: true };
  } catch (error) {
    console.error('❌ Validation error:', error);
    return { valid: false, error: 'Validation failed' };
  }
};

/**
 * 🔐 Complete secure bet request flow
 * Validates, encrypts, and signs the bet request
 * @param betPayload - Original bet data
 * @param userId - User ID
 * @returns Secure request or error
 */
export const prepareSecureBetRequest = (betPayload: any, userId: string) => {
  // Step 1: Validate payload
  const validation = validateBetPayload(betPayload);
  if (!validation.valid) {
    throw new Error(validation.error || 'Invalid bet payload');
  }
  
  // Step 2: Add additional security fields INCLUDING BETCODE for bet placement
  const BETCODE = process.env.REACT_APP_BETCODE || 'Yasfdgyys35466';
  const enhancedPayload = {
    ...betPayload,
    betcode: BETCODE, // 🔒 CRITICAL: Add betcode for backend validation (KEPT FOR BETTING)
    clientTimestamp: Date.now(),
    clientVersion: '1.0.0',
    platform: 'web'
  };
  
  // Step 3: Create secure request
  const secureRequest = createSecureBetRequest(enhancedPayload, userId);
  
  return secureRequest;
};

/**
 * 🔐 Prepare secure deposit/withdraw request
 * Encrypts and signs deposit/withdraw requests to prevent tampering
 * @param depositWithdrawData - Deposit/withdraw data
 * @param userId - User ID
 * @returns Secure request or error
 */
export const prepareSecureDepositWithdrawRequest = (depositWithdrawData: any, userId: string) => {
  try {
    console.log('🛡️ Creating secure deposit/withdraw request...');
    
    // Add additional security fields (NO BETCODE - using encryption instead)
    const enhancedPayload = {
      ...depositWithdrawData,
      clientTimestamp: Date.now(),
      clientVersion: '1.0.0',
      platform: 'web'
    };
    
    // Create secure request with encryption and signature
    const secureRequest = createSecureBetRequest(enhancedPayload, userId);
    
    console.log('✅ Secure deposit/withdraw request created');
    return secureRequest;
  } catch (error) {
    console.error('❌ Failed to create secure deposit/withdraw request:', error);
    throw error;
  }
};

export default {
  encryptBetPayload,
  signRequest,
  createSecureBetRequest,
  decryptResponse,
  validateBetPayload,
  prepareSecureBetRequest,
  prepareSecureDepositWithdrawRequest
};


